import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from "@clerk/clerk-react";
import axios from 'axios';
import {
    ChevronLeft,
    GraduationCap,
    Layers,
    RotateCw,
    ChevronRight
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Toaster } from "@/components/ui/sonner";
import { toast } from 'sonner';
import './Flashcard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudyTools = () => {
    const { documentId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();
    const [document, setDocument] = useState(null);
    const [activeTab, setActiveTab] = useState('quiz');
    const [quiz, setQuiz] = useState(null);
    const [flashcards, setFlashcards] = useState([]);
    const [currentCard, setCurrentCard] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchDocument = async () => {
            try {
                const response = await axios.get(`${API}/documents/${documentId}?user_id=${user?.id}`);
                setDocument(response.data);
            } catch (error) {
                console.error('Error:', error);
                navigate('/dashboard');
            }
        };

        if (user && documentId) {
            fetchDocument();
        }
    }, [documentId, navigate, user]);

    const generateQuiz = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${API}/quiz/generate`, {
                document_id: documentId,
                count: 5,
                user_id: user?.id
            });
            setQuiz(response.data.questions);
            toast.success('Quiz generated!');
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to generate quiz');
        } finally {
            setLoading(false);
        }
    };

    const generateFlashcards = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${API}/flashcards/generate`, {
                document_id: documentId,
                count: 10,
                user_id: user?.id
            });
            setFlashcards(response.data.flashcards);
            setCurrentCard(0);
            setFlipped(false);
            toast.success('Flashcards generated!');
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to generate flashcards');
        } finally {
            setLoading(false);
        }
    };

    const nextCard = () => {
        if (currentCard < flashcards.length - 1) {
            setCurrentCard(currentCard + 1);
            setFlipped(false);
        }
    };

    const prevCard = () => {
        if (currentCard > 0) {
            setCurrentCard(currentCard - 1);
            setFlipped(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-tr from-slate-50 to-white">
            <Toaster position="top-center" />
            <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dashboard')}
                            data-testid="back-btn"
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Back
                        </Button>
                        <div>
                            <h2 className="font-semibold text-slate-900">{document?.filename}</h2>
                            <p className="text-xs text-slate-500">Study Tools</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-12" data-testid="study-tools">
                <div className="flex gap-4 mb-8">
                    <Button
                        onClick={() => setActiveTab('quiz')}
                        data-testid="quiz-tab"
                        variant={activeTab === 'quiz' ? 'default' : 'outline'}
                        className={activeTab === 'quiz' ? 'bg-slate-900 text-white' : ''}
                    >
                        <GraduationCap className="w-4 h-4 mr-2" />
                        Quiz
                    </Button>
                    <Button
                        onClick={() => setActiveTab('flashcards')}
                        data-testid="flashcards-tab"
                        variant={activeTab === 'flashcards' ? 'default' : 'outline'}
                        className={activeTab === 'flashcards' ? 'bg-slate-900 text-white' : ''}
                    >
                        <Layers className="w-4 h-4 mr-2" />
                        Flashcards
                    </Button>
                </div>

                {activeTab === 'quiz' && (
                    <div>
                        {!quiz ? (
                            <div className="text-center py-12">
                                <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                                <p className="text-slate-600 mb-6">Generate a quiz from your document</p>
                                <Button
                                    onClick={generateQuiz}
                                    disabled={loading}
                                    data-testid="generate-quiz-btn"
                                    className="bg-blue-600 text-white hover:bg-blue-700"
                                >
                                    {loading ? 'Generating...' : 'Generate Quiz'}
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-6" data-testid="quiz-questions">
                                {quiz.map((q, idx) => (
                                    <Card key={idx} className="border border-slate-200">
                                        <CardHeader>
                                            <CardTitle className="text-lg">Question {idx + 1}</CardTitle>
                                            <p className="text-slate-700 mt-2">{q.question}</p>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2 mb-4">
                                                {q.options.map((option, optIdx) => (
                                                    <div
                                                        key={optIdx}
                                                        className="p-3 border border-slate-200 rounded hover:bg-slate-50 cursor-pointer"
                                                    >
                                                        {option}
                                                    </div>
                                                ))}
                                            </div>
                                            <details className="text-sm text-slate-600">
                                                <summary className="cursor-pointer font-medium text-blue-600">
                                                    Show Answer
                                                </summary>
                                                <p className="mt-2">
                                                    <strong>Correct:</strong> {q.correct_answer}
                                                </p>
                                                <p className="mt-1">{q.explanation}</p>
                                            </details>
                                        </CardContent>
                                    </Card>
                                ))}
                                <Button
                                    onClick={generateQuiz}
                                    variant="outline"
                                    data-testid="regenerate-quiz-btn"
                                    className="w-full"
                                >
                                    <RotateCw className="w-4 h-4 mr-2" />
                                    Generate New Quiz
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'flashcards' && (
                    <div>
                        {flashcards.length === 0 ? (
                            <div className="text-center py-12">
                                <Layers className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                                <p className="text-slate-600 mb-6">Generate flashcards from your document</p>
                                <Button
                                    onClick={generateFlashcards}
                                    disabled={loading}
                                    data-testid="generate-flashcards-btn"
                                    className="bg-blue-600 text-white hover:bg-blue-700"
                                >
                                    {loading ? 'Generating...' : 'Generate Flashcards'}
                                </Button>
                            </div>
                        ) : (
                            <div className="flashcard-container" data-testid="flashcard-viewer">
                                <div
                                    className={`flashcard-wrapper ${flipped ? 'flipped' : ''}`}
                                    onClick={() => setFlipped(!flipped)}
                                    data-testid="flashcard"
                                >
                                    <div className="flashcard-side front">
                                        <div className="flashcard-content">
                                            <p className="flashcard-label">FRONT</p>
                                            <p className="flashcard-text">{flashcards[currentCard].front}</p>
                                        </div>
                                    </div>
                                    <div className="flashcard-side back">
                                        <div className="flashcard-content">
                                            <p className="flashcard-label">BACK</p>
                                            <p className="flashcard-text">{flashcards[currentCard].back}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between mb-4">
                                    <Button
                                        onClick={prevCard}
                                        disabled={currentCard === 0}
                                        data-testid="prev-card-btn"
                                        variant="outline"
                                    >
                                        <ChevronLeft className="w-4 h-4 mr-1" />
                                        Previous
                                    </Button>
                                    <span className="text-slate-600">
                                        {currentCard + 1} / {flashcards.length}
                                    </span>
                                    <Button
                                        onClick={nextCard}
                                        disabled={currentCard === flashcards.length - 1}
                                        data-testid="next-card-btn"
                                        variant="outline"
                                    >
                                        Next
                                        <ChevronRight className="w-4 h-4 ml-1" />
                                    </Button>
                                </div>

                                <Button
                                    onClick={generateFlashcards}
                                    variant="outline"
                                    data-testid="regenerate-flashcards-btn"
                                    className="w-full"
                                >
                                    <RotateCw className="w-4 h-4 mr-2" />
                                    Generate New Set
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default StudyTools;
